"use client";

import { ArrowDownRight, Bike, Coffee, Compass, Mountain, Route, Snowflake, Sparkles, Sun, TreePine, Waves, type LucideIcon } from "lucide-react";
import type { AreaSport } from "@/lib/planner/graph-types";
import { dayStylesForSport, type DayStyle } from "@/lib/planner/day-styles";

type Props = {
  sport: AreaSport;
  onPickStyle: (key: DayStyle) => void;
  onBuildYourOwn: () => void;
  busy?: boolean;
};

const presentation: Record<DayStyle, { icon: LucideIcon; description: string }> = {
  "locals-best": { icon: Sparkles, description: "Let the conditions lead. A balanced day matched to your ability." },
  "cruisy-classics": { icon: Waves, description: "Find your rhythm on long, flowing runs." },
  "steep-technical": { icon: Mountain, description: "Seek out steeper pitches and technical turns within your limits." },
  "cover-ground": { icon: Compass, description: "Explore more of the mountain, with fresh scenery along the way." },
  "quiet-day": { icon: TreePine, description: "Head for calmer corners and lifts with shorter estimated queues." },
  "sun-and-views": { icon: Sun, description: "Chase sunny slopes and take in the views." },
  "easy-social": { icon: Coffee, description: "Easy-going runs, coffee stops and time together." },
  "best-snow": { icon: Snowflake, description: "Follow the best forecast snow, even if it means another lap." },
  "locals-best-ride": { icon: Sparkles, description: "A balanced ride matched to your ability, bike and trail forecast." },
  "flow-day": { icon: Waves, description: "Settle into smooth trails and flowing turns." },
  "technical-enduro": { icon: Mountain, description: "Find rougher, steeper trails within your limits." },
  "big-descents": { icon: ArrowDownRight, description: "Make the most of uplift with long descents and short linking climbs." },
  "xc-adventure": { icon: Bike, description: "Earn your turns and explore further under your own power." },
  "quiet-uplift": { icon: TreePine, description: "Spend less time in estimated queues and more time riding." },
  "easy-social-ride": { icon: Coffee, description: "Keep it relaxed, with easier trails and time for a break." },
  "best-dirt": { icon: Route, description: "Follow the best forecast trail conditions, with repeat laps welcome." },
};

export function PlanMyDayEntry({ sport, onPickStyle, onBuildYourOwn, busy = false }: Props) {
  const styles = dayStylesForSport(sport);
  return <section className="rounded-[18px] border border-border-light bg-white p-5 md:p-6" aria-labelledby="day-style-heading">
    <h2 id="day-style-heading" className="font-heading text-[22px] font-bold text-navy">What would make your day?</h2>
    <p className="mt-1 text-sm text-text-muted">Choose a mood and we’ll find your {sport === "mtb" ? "ride" : "route"}. Your ability, times and must-do stop come first.</p>
    <div className="mt-5 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
      {styles.map((style, index) => {
        const { icon: Icon, description } = presentation[style.key];
        const recommended = index === 0;
        return <button key={style.key} type="button" disabled={busy} onClick={() => onPickStyle(style.key)}
          className={`group flex min-h-[96px] items-start gap-3 rounded-[14px] p-4 text-left transition-colors focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-teal disabled:opacity-50 ${recommended ? "border border-navy bg-navy text-white hover:bg-navy-hover sm:col-span-2" : "border border-border bg-white text-navy hover:border-teal hover:bg-teal/5"}`}>
          <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${recommended ? "bg-white/10 text-teal-light" : "bg-warm-bg text-teal"}`}><Icon size={22} strokeWidth={1.7} aria-hidden /></span>
          <span className="min-w-0"><span className="block text-[15px] font-bold">{style.label}</span>
            <span className={`mt-1 block text-[13px] leading-relaxed ${recommended ? "text-white/80" : "text-text-muted"}`}>{description}</span>
          </span>
        </button>;
      })}
    </div>
    <button type="button" onClick={onBuildYourOwn} disabled={busy} className="mt-4 inline-flex min-h-[44px] w-full items-center justify-center rounded-sm border border-border bg-white px-4 text-sm font-semibold text-navy hover:border-teal focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal disabled:opacity-50">
      {sport === "mtb" ? "Build your own ride" : "Build your own day"}
    </button>
  </section>;
}
